import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { TownSelectItem } from 'src/components/TownSelect/TownSelect';
import {
  getAdminState,
  getAdminTownSelectItem,
} from 'src/store/admin/selectors';
import { getBuildingsState } from 'src/store/buildings/selectors';
import { changeBuilding, addBuilding } from 'src/store/buildings/services';
import { useAppDispatch } from 'src/store/useAppDispatch';

export interface AdministratorBuildingFormValues {
  id: number | undefined;
  address: string;
  town: TownSelectItem | undefined;
  enabled: boolean;
  disabledReason: string;
  isTestData: boolean;
}

const defaultFormValues: AdministratorBuildingFormValues = {
  id: undefined,
  address: '',
  town: undefined,
  enabled: true,
  disabledReason: '',
  isTestData: false,
};

export interface AdministratorBuildingFormProps {
  children?: ReactNode;
}

export const AdministratorBuildingForm: FC<AdministratorBuildingFormProps> = ({
  children,
}) => {
  const dispatch = useAppDispatch();

  const { selectedBuildingId } = useSelector(getAdminState);
  const { buildingsById } = useSelector(getBuildingsState);
  const town = useSelector(getAdminTownSelectItem);

  const save = useCallback(
    async (values: AdministratorBuildingFormValues) => {
      if (!values.town?.value.townId) return false;

      const params = {
        townId: values.town.value.townId,
        address: values.address,
        ownEnabled: values.enabled,
        ownDisabledReason: values.disabledReason,
        testData: values.isTestData,
      };

      if (values.id) {
        await dispatch(changeBuilding({ buildingId: values.id, ...params }));
      } else {
        await dispatch(addBuilding(params));
      }

      return true;
    },
    [dispatch],
  );

  const initialValues = useMemo((): AdministratorBuildingFormValues => {
    if (!selectedBuildingId)
      return {
        ...defaultFormValues,
        town,
      };

    const building = buildingsById.get(selectedBuildingId);

    if (building)
      return {
        id: building.buildingId,
        town,
        address: building.address,
        enabled: building.ownEnabled ?? false,
        disabledReason: building.ownDisabledReason ?? '',
        isTestData: building.testData ?? false,
      };

    return {
      ...defaultFormValues,
      town,
    };
  }, [buildingsById, selectedBuildingId, town]);

  return (
    <Form onSubmit={save} initialValues={initialValues}>
      {() => children}
    </Form>
  );
};
